import React from 'react';
import brandConfig from '../../config/brandConfig';
import { Building2, BarChart3, Eye, Users, DollarSign, Shield, Zap, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';

const RealtorSection = () => {
  const dashboardUrl = brandConfig.dashboardUrl;

  const features = [
    {
      icon: <BarChart3 className="w-7 h-7" />,
      title: "Gestion Completa de Ventas",
      description: "Administra todos tus lotes desde un solo panel. Controla inventario, precios, disponibilidad y seguimiento de clientes.",
      gradient: "from-orange-500 to-amber-500",
      lightBg: "bg-orange-50 dark:bg-orange-950/20"
    },
    {
      icon: <Eye className="w-7 h-7" />,
      title: "Visores Interactivos Avanzados",
      description: "Muestra tus propiedades con mapas interactivos, mediciones precisas y detalles tecnicos completos.",
      gradient: "from-emerald-500 to-teal-500",
      lightBg: "bg-emerald-50 dark:bg-emerald-950/20"
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: "Acceso a Miles de Compradores",
      description: "Conecta con miles de compradores activos que buscan propiedades en toda Latinoamerica.",
      gradient: "from-blue-500 to-indigo-500",
      lightBg: "bg-blue-50 dark:bg-blue-950/20"
    },
    {
      icon: <TrendingUp className="w-7 h-7" />,
      title: "Analytics y Reportes",
      description: "Obten insights detallados sobre el rendimiento de tus propiedades y comportamiento de compradores.",
      gradient: "from-violet-500 to-purple-500",
      lightBg: "bg-violet-50 dark:bg-violet-950/20"
    }
  ];

  const benefits = [
    { icon: <Shield className="w-5 h-5" />, title: "Transacciones Seguras", description: "Garantia total en cada operacion" },
    { icon: <Zap className="w-5 h-5" />, title: "Publicacion Instantanea", description: "Sube tus lotes y aparecen inmediatamente" },
    { icon: <DollarSign className="w-5 h-5" />, title: "Sin Comisiones Ocultas", description: "Tarifas transparentes y competitivas" },
    { icon: <CheckCircle className="w-5 h-5" />, title: "Soporte Dedicado", description: "Equipo especializado para inmobiliarias" }
  ];

  const stats = [
    { number: "150+", label: "Inmobiliarias Activas" },
    { number: "2,500+", label: "Lotes Publicados" },
    { number: "15,000+", label: "Compradores Registrados" },
    { number: "98%", label: "Satisfaccion" }
  ];

  return (
    <div className="py-24 lg:py-32 bg-gray-50 dark:bg-gray-950 relative overflow-hidden">
      {/* Subtle background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-[0.03]" style={{
        background: `radial-gradient(circle, ${brandConfig.primaryColor} 0%, transparent 70%)`
      }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 mb-6">
            <Building2 className="w-4 h-4" style={{ color: brandConfig.primaryColor }} />
            <span className="text-sm font-bold" style={{ color: brandConfig.primaryColor }}>Para Inmobiliarias</span>
          </div>
          <h2 className="text-4xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 tracking-tight">
            Todo lo que necesitas para
            <span className="block font-display italic mt-2" style={{ color: brandConfig.primaryColor }}>vender mas</span>
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Unete a la plataforma inmobiliaria mas completa de Latinoamerica. Gestiona tus ventas,
            muestra tus lotes con gran detalle y conecta con miles de compradores.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`${feature.lightBg} rounded-3xl p-8 lg:p-10 border border-gray-100 dark:border-gray-800 card-hover group relative overflow-hidden`}
            >
              <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full opacity-10 bg-gradient-to-br ${feature.gradient}" />
              <div className="relative">
                <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-2xl mb-6 text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl lg:text-2xl font-extrabold text-gray-900 dark:text-white mb-3 tracking-tight">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Benefits */}
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-10 lg:p-14 shadow-xl shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-gray-800 mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-3 tracking-tight">
              Por que elegir {brandConfig.domain}?
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
              Las mejores herramientas y servicios para potenciar tu negocio inmobiliario
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-800 mb-4 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30 transition-colors duration-300">
                  <div className="text-gray-500 dark:text-gray-400 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300">
                    {benefit.icon}
                  </div>
                </div>
                <h4 className="text-base font-bold text-gray-900 dark:text-white mb-1">{benefit.title}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="relative rounded-3xl p-10 lg:p-14 text-white overflow-hidden grain-overlay" style={{
          background: 'linear-gradient(135deg, #102a43 0%, #243b53 50%, #102a43 100%)'
        }}>
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `radial-gradient(circle at 30% 50%, ${brandConfig.primaryColor}40 0%, transparent 50%), radial-gradient(circle at 70% 50%, ${brandConfig.secondaryColor}30 0%, transparent 50%)`
          }} />
          <div className="relative z-10">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-extrabold mb-3 tracking-tight">
                Unete a las inmobiliarias lideres
              </h3>
              <p className="text-white/60 max-w-2xl mx-auto">
                La plataforma preferida por las principales inmobiliarias de Latinoamerica
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center glass rounded-2xl p-6">
                  <div className="text-3xl lg:text-4xl font-extrabold text-white mb-1 tracking-tight">{stat.number}</div>
                  <div className="text-white/50 text-sm font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-20">
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/10 rounded-3xl p-10 lg:p-14 border border-orange-100 dark:border-orange-900/30">
            <h3 className="text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white mb-4 tracking-tight">
              Listo para potenciar tu negocio?
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              Comienza a gestionar tus lotes de manera profesional y conecta con compradores
              calificados desde el primer dia.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`${dashboardUrl}/iniciar-sesion`}
                className="inline-flex items-center justify-center gap-2 text-white px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:scale-[1.02]"
                style={{ background: brandConfig.getPrimaryGradient() }}
              >
                <Building2 className="w-5 h-5" />
                Registrar Mi Inmobiliaria
              </a>
              <a
                href="/proyectos/residencial-santa-clara"
                className="inline-flex items-center justify-center gap-2 bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:border-orange-300 dark:hover:border-orange-700 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300"
              >
                <ArrowRight className="w-5 h-5" />
                Ver Demo en Vivo
              </a>
            </div>

            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-gray-400 dark:text-gray-500">
              {['Registro gratuito', 'Sin compromiso', 'Soporte incluido'].map((text, i) => (
                <div key={i} className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-medium">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealtorSection;
